// # 반응 캐시 — 줄마다 붙은 이모지 집계
//
// 메시지 본문에는 반응이 실리지 않는다(`Message` 에 없다). 화면에 보이는 줄들의
// 반응을 **한 번에** 센다(`POST …/reactions {seqs}`) — 줄마다 묻으면 50줄에 50 왕복이다.
//
// ## 사건은 수를 싣지 않는다
// `reacted` 는 "누가 무엇을 붙였다/뗐다" 만 말한다. 각자 제 화면을 ±1 하고, 같은
// 사람이 같은 이모지를 두 번 붙이는 일은 서버가 막으므로(바뀌었을 때만 사건이 온다)
// 어긋날 일이 드물다. 그래도 내가 누른 것은 서버 답을 기다리지 않고 먼저 그린다 —
// 실패하면 그 줄만 다시 센다.

import type { ChannelId, Event, Seq, UserId } from '@maru/protocol'
import type { MaruApi, Tally } from './api.ts'

export type TallyMap = ReadonlyMap<Seq, readonly Tally[]>

const EMPTY: TallyMap = new Map()

interface Held {
  by_seq: Map<Seq, readonly Tally[]>
  /** 아직 안 센 줄. 마이크로태스크 하나로 묶어 한 번에 묻는다. */
  wanted: Set<Seq>
  asking: Promise<void> | null
  view: TallyMap
}

export class Reactions {
  #api: MaruApi
  #held = new Map<ChannelId, Held>()
  #listeners = new Set<() => void>()
  /** 사건의 `who` 가 나인지 알아야 `mine` 을 맞춘다. 로그인 뒤 껍데기가 심는다. */
  me: UserId | null = null

  constructor(api: MaruApi) {
    this.#api = api
  }

  subscribe = (listen: () => void): (() => void) => {
    this.#listeners.add(listen)
    return () => void this.#listeners.delete(listen)
  }

  /** 같은 상태면 같은 객체. */
  slice = (channel: ChannelId | null): TallyMap => {
    if (channel === null) return EMPTY
    return this.#held.get(channel)?.view ?? EMPTY
  }

  /** 이 줄들이 보인다. 모르는 것만 묶어서 한 번 센다. */
  need(channel: ChannelId, seqs: readonly Seq[]): void {
    const h = this.#hold(channel)
    let added = false
    for (const s of seqs) {
      if (h.by_seq.has(s) || h.wanted.has(s)) continue
      h.wanted.add(s)
      added = true
    }
    if (added) this.#ask(channel, h)
  }

  /** 끊긴 사이 반응이 바뀌었을 수 있다. 센 적 있는 줄을 **한 번에** 다시 센다. */
  refresh(channel: ChannelId): void {
    const h = this.#held.get(channel)
    if (h === undefined || h.by_seq.size === 0) return
    for (const s of h.by_seq.keys()) h.wanted.add(s)
    this.#ask(channel, h)
  }

  on_event(e: Event): void {
    if (e.t !== 'reacted') return
    const h = this.#held.get(e.channel)
    if (h === undefined || !h.by_seq.has(e.seq)) return
    const list = h.by_seq.get(e.seq) ?? []
    if (already(list, e.emoji, e.added, e.who, e.who === this.me)) return
    h.by_seq.set(e.seq, bump(list, e.emoji, e.added, e.who === this.me, e.who))
    this.#publish(h)
  }

  /**
   * 내가 누른다. 화면을 먼저 움직이고 서버에 말한다. 서버가 거절하면 그 줄만 다시 센다 —
   * 되돌리기를 손으로 계산하면 그 사이 온 사건과 꼬인다.
   */
  async toggle(channel: ChannelId, seq: Seq, emoji: string): Promise<void> {
    const h = this.#hold(channel)
    const before = h.by_seq.get(seq) ?? []
    const mine = before.find((t) => t.emoji === emoji)?.mine ?? false
    h.by_seq.set(seq, bump(before, emoji, !mine, true, this.me ?? undefined))
    this.#publish(h)
    try {
      if (mine) await this.#api.unreact(channel, seq, emoji)
      else await this.#api.react(channel, seq, emoji)
    } catch (e) {
      h.by_seq.delete(seq)
      h.wanted.add(seq)
      this.#ask(channel, h)
      throw e
    }
  }

  #hold(channel: ChannelId): Held {
    let h = this.#held.get(channel)
    if (h === undefined) {
      h = { by_seq: new Map(), wanted: new Set(), asking: null, view: EMPTY }
      this.#held.set(channel, h)
    }
    return h
  }

  #ask(channel: ChannelId, h: Held): void {
    if (h.asking !== null) return
    h.asking = Promise.resolve().then(async () => {
      try {
        while (h.wanted.size > 0) {
          const seqs = [...h.wanted]
          h.wanted.clear()
          const got = await this.#api.reaction_count(channel, seqs)
          const found = new Map(got.map((r) => [r.seq, r.reactions]))
          // 답에 없는 seq 는 반응이 없는 것이다. 빈 배열로 적어 두어야 다시 묻지 않는다.
          for (const s of seqs) h.by_seq.set(s, found.get(s) ?? [])
          this.#publish(h)
        }
      } catch {
        // 다음 `need` 가 다시 묻는다. 반응 집계 때문에 화면에 오류를 띄우지 않는다.
      } finally {
        h.asking = null
      }
    })
  }

  #publish(h: Held): void {
    h.view = new Map(h.by_seq)
    for (const l of this.#listeners) l()
  }
}

/**
 * 이 사건이 이미 화면에 들어 있는가. 내가 누른 것은 서버 답 전에 먼저 그리므로, 곧이어 오는
 * 내 사건을 또 더하면 한 번 누른 하트가 2 가 된다(3차 J6 — 사건이 두 번 닿으면 3). 내 것은
 * `mine` 이, 남의 것은 이름 목록이 판정한다. 이름 목록은 앞의 20명뿐이라, 목록이 수보다 짧으면
 * 없는 이름이 정말 없는지 모른다 — 그때는 믿고 센다.
 */
export function already(
  list: readonly Tally[],
  emoji: string,
  added: boolean,
  user: UserId,
  by_me: boolean,
): boolean {
  const one = list.find((t) => t.emoji === emoji)
  if (by_me) return (one?.mine ?? false) === added
  if (one === undefined) return !added
  const who = one.who
  if (who === undefined) return false
  if (added) return who.includes(user)
  return who.length >= one.count && !who.includes(user)
}

/** 한 줄의 집계에 하나를 더하거나 뺀다. 0 이 되면 지운다. 순서는 유지한다. */
export function bump(
  list: readonly Tally[],
  emoji: string,
  added: boolean,
  by_me: boolean,
  user?: UserId,
): readonly Tally[] {
  const at = list.findIndex((t) => t.emoji === emoji)
  if (at < 0) {
    if (!added) return list
    const fresh: Tally = { emoji, count: 1, mine: by_me }
    if (user !== undefined) fresh.who = [user]
    return [...list, fresh]
  }
  const one = list[at] as Tally
  const count = one.count + (added ? 1 : -1)
  const next = [...list]
  if (count <= 0) {
    next.splice(at, 1)
    return next
  }
  const moved: Tally = { emoji, count, mine: by_me ? added : one.mine }
  const who = user === undefined ? one.who : shift_who(one.who ?? [], user, added)
  if (who !== undefined) moved.who = who
  next[at] = moved
  return next
}

/** 이름 목록에 넣거나 뺀다. 서버와 같이 앞의 20명까지만 든다. */
function shift_who(who: readonly UserId[], user: UserId, added: boolean): UserId[] {
  const rest = who.filter((u) => u !== user)
  return added ? [...rest, user].slice(0, 20) : rest
}
